<template>
  <Form
    :form-id="formId"
    :initial-values="initialValues"
    action="#"
    :class="{ dirty }"
  >
    <HiddenInput input-id="id" />

    <VPInput
      ref="descriptionRef"
      :disabled="disabled"
      input-id="description"
      placeholder="Add description"
      class="double-f"
    />
    <VPInput
      ref="ticketsSoldRef"
      type="integer"
      :disabled="disabled"
      input-id="ticketsSold"
      placeholder="Add number"
      class="text-right"
    />
    <VPPriceInput
      ref="priceRef"
      :disabled="disabled"
      input-id="price"
      placeholder="Add price"
      hide-empty-input
    />

    <div class="right-field">
      {{ totalDisplay }}
    </div>
  </Form>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { formatCurrency } from '@/helpers/currency';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPInput from '@/components/form/VPInput.vue';

const descriptionRef = ref<VPInput>(null);
const ticketsSoldRef = ref<VPInput>(null);
const priceRef = ref<VPPriceInput>(null);

const formsStore = useFormsStore();

interface Props {
  formId: string;
  initialValues: unknown;
  total: number;
  disabled?: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'blur'): void;
  (e: 'save'): void;
  (e: 'clear'): void;
}>();

const totalDisplay = computed(() => {
  if (!formsStore.fieldGetValue(props.formId, 'id')) {
    return '';
  }

  const total = isNaN(props.total) ? 0 : props.total;
  return formatCurrency(total, { onZero: '-' });
});

const refs = [descriptionRef, ticketsSoldRef, priceRef];

const activeElement = ref<EventTarget | null>(null);

const focusChanged = (e: Event) => {
  activeElement.value = e.target;
};

onMounted(() => {
  document.addEventListener('focusin', focusChanged);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusChanged);
});

const hasFocus = computed(() =>
  refs.some(r => r.value?.inputRef && activeElement.value && r.value?.inputRef === activeElement.value),
);

const hasValue = computed(() => {
  return ['description', 'ticketsSold', 'price'].some(f => formsStore.fieldGetValue(props.formId, f));
});

watch(hasFocus, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (hasValue.value) {
      emit('blur');
    } else {
      emit('clear');
    }
  }
});

const dirty = computed(() => formsStore.isFormDirty(props.formId));

// const onSubmit = () => {
//   emit('save');
// };
</script>
